import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { PageBackground } from "@/components/ui/page-background";
import { AppHeader } from "@/components/app-header";
import { ArrowLeft, Calendar, Clock, Music, Trophy, TrendingUp, Users } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { useAuth } from "@/hooks/use-auth";

interface Participant {
  id: number;
  name: string;
  email: string;
  phone: string;
  instagram: string;
  organisationId: number;
  lookingForBand: boolean;
  createdAt: string;
}

interface PerformanceHistory {
  eventId: number;
  eventName: string;
  eventDate: string;
  eventType: string;
  venue: string;
  performanceCount: number;
  timeOnStage: number;
  instruments: string[];
  tier: string | null;
  performerType: string | null;
  played: boolean;
  attended: boolean;
  raffleWins: number;
  raffleEntrances: number;
}

interface PerformerHistoryData {
  participant: Participant;
  summary: {
    totalPerformances: number;
    totalStageTime: number;
    eventsAttended: number;
    avgStageTime: number;
    topInstruments: Array<{ instrument: string; count: number }>;
  };
  performanceHistory: PerformanceHistory[];
}

export default function PerformerHistory() {
  const { participantId, organisationId } = useParams<{ participantId: string; organisationId: string }>();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  
  // Get URL parameters to preserve analytics state when going back
  const urlParams = new URLSearchParams(window.location.search);
  const returnTab = urlParams.get('tab') || 'overview';
  const returnEventType = urlParams.get('eventType') || '';
  const returnEventId = urlParams.get('eventId') || '';
  const returnSearch = urlParams.get('search') || '';
  const fromParticipants = urlParams.get('from') === 'participants';
  
  // Construct back URL with preserved state
  const backUrl = `/analytics/${organisationId}?tab=${returnTab}&eventType=${returnEventType}&eventId=${returnEventId}&search=${returnSearch}`;
  
  const currentOrgId = organisationId ? parseInt(organisationId) : null;
  
  // Get current organization info
  const currentOrg = user?.organisationUsers?.find(ou => ou.organisation.id === currentOrgId)?.organisation;
  
  // Check if user has access to this organization
  const hasAccess = user?.role === 'god' || user?.organisationUsers?.some(ou => 
    ou.organisation.id === currentOrgId && (ou.role === 'owner' || ou.role === 'operator')
  );

  const { data: historyData, isLoading } = useQuery<PerformerHistoryData>({
    queryKey: [`/api/stats/performer-history/${participantId}?organisationId=${currentOrgId}`],
    enabled: !!participantId && !!currentOrgId && hasAccess,
    staleTime: 0
  });

  const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'jam': return 'bg-blue-500';
      case 'openmic': return 'bg-green-500';
      case 'generic': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  if (!hasAccess || !currentOrgId) {
    return (
      <PageBackground>
        <AppHeader username={user?.username} />
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <h2 className="text-xl font-semibold mb-2 text-slate-900 drop-shadow-sm">Access Denied</h2>
            <p className="text-slate-600 mb-4 drop-shadow-sm">You don't have permission to view this performer's history.</p>
            <Button onClick={() => setLocation('/')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </PageBackground>
    );
  }

  if (isLoading) {
    return (
      <PageBackground>
        <AppHeader username={user?.username} />
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <div className="text-lg text-slate-900 drop-shadow-sm">Loading performer history...</div>
          </div>
        </div>
      </PageBackground>
    );
  }

  if (!historyData) {
    return (
      <PageBackground>
        <AppHeader username={user?.username} />
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <h2 className="text-xl font-semibold mb-2 text-slate-900 drop-shadow-sm">Performer not found</h2>
            <Button onClick={() => setLocation(backUrl)} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </PageBackground>
    );
  }

  const { participant, summary, performanceHistory } = historyData;

  // Prepare chart data for performance over time
  const performanceOverTime = performanceHistory
    .sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime())
    .map(performance => ({
      date: formatDate(performance.eventDate),
      performances: performance.performanceCount,
      stageTime: performance.timeOnStage,
      eventName: performance.eventName
    }));

  return (
    <PageBackground>
      <AppHeader username={user?.username} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 relative z-10">
        <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 mt-2 -mb-2">

          <div>
            <h1 className="text-3xl font-bold drop-shadow-sm">
              {participant.name}
            </h1>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Event Signups</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.eventsAttended}</div>
              <p className="text-xs text-muted-foreground">
                Total events participated in
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Performances</CardTitle>
              <Trophy className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.totalPerformances}</div>
              <p className="text-xs text-muted-foreground">
                Times performed on stage
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Stage Time</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatMinutes(summary.totalStageTime)}</div>
              <p className="text-xs text-muted-foreground">
                Cumulative time performing
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Performance</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatMinutes(summary.avgStageTime)}</div>
              <p className="text-xs text-muted-foreground">
                Average time per performance
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Performance Chart and Contact Info */}
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Performance Activity Over Time</CardTitle>
                <CardDescription>
                  Stage time and performance count by event
                </CardDescription>
              </CardHeader>
              <CardContent>
                {performanceOverTime.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={performanceOverTime}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(label) => `Event: ${label}`}
                        formatter={(value, name) => [
                          name === 'stageTime' ? formatMinutes(value as number) : value,
                          name === 'stageTime' ? 'Stage Time' : 'Performances'
                        ]}
                      />
                      <Line type="monotone" dataKey="performances" stroke="#3b82f6" name="Performances" />
                      <Line type="monotone" dataKey="stageTime" stroke="#ef4444" name="Stage Time (minutes)" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    No performance history available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {participant.email && (
                  <div>
                    <p className="text-sm font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">{participant.email}</p>
                  </div>
                )}
                {participant.phone && (
                  <div>
                    <p className="text-sm font-medium">Phone</p>
                    <p className="text-sm text-muted-foreground">{participant.phone}</p>
                  </div>
                )}
                {participant.instagram && (
                  <div>
                    <p className="text-sm font-medium">Instagram</p>
                    <p className="text-sm text-muted-foreground">@{participant.instagram}</p>
                  </div>
                )}
                {participant.lookingForBand && (
                  <div>
                    <Badge variant="secondary" className="bg-green-500/20 text-green-700">
                      Looking for Band
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Instruments */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Music className="h-5 w-5" />
                  Preferred Instruments
                </CardTitle>
                <CardDescription>
                  Most frequently played instruments
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {summary.topInstruments?.map((instrument, index) => (
                    <div key={instrument.instrument} className="flex items-center justify-between">
                      <span className="text-sm capitalize">{instrument.instrument}</span>
                      <Badge variant="outline">
                        {instrument.count} times
                      </Badge>
                    </div>
                  )) || (
                    <div className="text-center text-muted-foreground py-4">
                      No instrument data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Event History */}
        <Card>
          <CardHeader>
            <CardTitle>Event History</CardTitle>
            <CardDescription>
              Complete performance record across all events
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {performanceHistory
                .sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime())
                .map((performance, index) => (
                <div key={`${performance.eventId}-${index}`} className="p-4 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold">{performance.eventName}</h4>
                        <div className={`w-2 h-2 rounded-full ${getEventTypeColor(performance.eventType)}`} />
                        <span className="text-xs text-muted-foreground capitalize">{performance.eventType}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(performance.eventDate)} • {performance.venue}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 mb-1">
                        {performance.played && (
                          <Badge variant="default" className="bg-green-500/20 text-green-700 border-green-500/30">
                            Performed
                          </Badge>
                        )}
                        {performance.attended && !performance.played && (
                          <Badge variant="secondary" className="bg-blue-500/20 text-blue-700 border-blue-500/30">
                            Attended
                          </Badge>
                        )}
                      </div>
                      {performance.tier && (
                        <Badge variant="outline" className="text-xs">
                          {performance.tier}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Performances</p>
                      <p className="font-medium">{performance.performanceCount}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Stage Time</p>
                      <p className="font-medium">{formatMinutes(performance.timeOnStage)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">
                        {performance.eventType === 'openmic' ? 'Performer Type' : 'Instruments'}
                      </p>
                      <p className="font-medium capitalize">
                        {performance.eventType === 'openmic' 
                          ? (performance.performerType || 'Not specified')
                          : (performance.instruments.length > 0 ? performance.instruments.join(', ') : 'None')
                        }
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Raffle</p>
                      <p className="font-medium">
                        {performance.raffleWins > 0 ? `${performance.raffleWins} wins` : 'No wins'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              
              {performanceHistory.length === 0 && (
                <div className="text-center text-muted-foreground py-8">
                  No performance history available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        </div>
      </main>
    </PageBackground>
  );
}